import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  CurrentUserQuery,
  NotificationPreferenceTopic,
  RegisterPushSubscriptionGQL,
  UpdateNotificationPreferenceGQL,
  UpdateNotificationPreferenceInput,
} from '@/common/graphql/generated';
import { AuthStateService } from '@/common/services/auth/auth-state.service';
import { environment } from '../../../environments/environment';

type NotificationOverride = Partial<
  Record<
    NotificationPreferenceTopic,
    Partial<Pick<UpdateNotificationPreferenceInput, NotificationChannelField>>
  >
>;
type NotificationChannelField = 'emailEnabled' | 'smsEnabled' | 'pushEnabled';
/** Local (this browser) push state, independent of the account preference. */
type DevicePushState = 'subscribed' | 'opted-out' | 'none';
type PushToggleAction = 'disable' | 'register';
type PushToggleContext = {
  accountEnabled: boolean;
  accountId: string;
  /**
   * Push cleanup epoch captured when the toggle started, before its first
   * await. Any logout cleanup during the toggle (including the initial SDK
   * load inside `getDevicePushState()`) invalidates the action.
   */
  cleanupEpoch: number;
};
export type OneSignalSdk = {
  init: (options: { appId: string }) => Promise<void>;
  Notifications: {
    permission: boolean;
    // OneSignal v16 resolves after the prompt without necessarily returning
    // the resulting boolean, so callers must re-read `permission`.
    requestPermission: () => Promise<boolean | void>;
  };
  User: {
    PushSubscription: {
      id?: string | null;
      /** False while opted out (e.g. permission revoked) even if an id exists. */
      optedIn?: boolean;
      optIn: () => Promise<void>;
      optOut?: () => Promise<void>;
    };
  };
};

declare global {
  interface Window {
    OneSignalDeferred?: Array<
      (oneSignal: OneSignalSdk) => void | Promise<void>
    >;
  }
}

const ONE_SIGNAL_SCRIPT_ID = 'onesignal-sdk';
const ONE_SIGNAL_SCRIPT_SRC =
  'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
/** Document-wide shared SDK load promise; see `loadOneSignal()`. */
let oneSignalSdkPromise: Promise<OneSignalSdk> | undefined;
/**
 * Init results per loaded SDK instance. The v16 SDK is document-wide and
 * rejects a second `init()`, so every component instance must share the
 * first instance's outcome (including a failed init, which is dropped so a
 * later attempt can retry).
 */
const oneSignalInitPromises = new WeakMap<OneSignalSdk, Promise<void>>();
/**
 * Per loaded SDK, the accounts whose local opt-in succeeded but whose
 * backend association failed. Keyed by the loaded SDK (shared across
 * component instances, like the init results) so a recreated settings
 * component still knows this browser must re-register rather than read as
 * "subscribed" to the account. Scoped to account ids: a failure for one
 * account must not change how another signed-in account's switch behaves
 * on the same browser.
 */
const pushRegistrationsPending = new WeakMap<OneSignalSdk, Set<string>>();
/**
 * Per loaded SDK, accounts whose backend association has been confirmed for
 * the current local subscription id. This keeps a local OneSignal
 * subscription created by account A from being treated as proof that account
 * B receives pushes on this browser.
 */
const confirmedPushRegistrations = new WeakMap<
  OneSignalSdk,
  Map<string, string>
>();
/**
 * Bumped whenever logout / forced-expiry cleanup opts this browser's push
 * subscription out. A registration captures it before the SDK load and the
 * permission prompt, then aborts if it changed: a stale settings component
 * must not opt the browser back in - nor re-add the registration markers
 * cleanup rewrote - after the session it belonged to has signed out.
 */
let pushCleanupEpoch = 0;
/**
 * Storage marker for accounts whose browser registration is not confirmed
 * with the backend, as a JSON array of account ids. Survives a page reload
 * (the local OneSignal subscription does too), so a reloaded settings page
 * retries the registration instead of reading the surviving local
 * subscription as "this browser receives the account's push" and disabling
 * the preference account-wide - but only for the account that actually needs
 * it. Logout cleanup also leaves this marker for the account it opted out, so
 * signing back in restores this browser instead of disabling push for the
 * user's other devices.
 */
export const PENDING_PUSH_REGISTRATION_STORAGE_KEY =
  'mistbelle.pushRegistration.pending';
export const CONFIRMED_PUSH_REGISTRATION_STORAGE_KEY =
  'mistbelle.pushRegistration.confirmed';

function readStoredJson<T>(key: string, fallback: T): T {
  try {
    if (typeof localStorage === 'undefined') return fallback;
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function readPendingPushAccountIds(): string[] {
  const parsed: unknown = readStoredJson(
    PENDING_PUSH_REGISTRATION_STORAGE_KEY,
    [],
  );
  return Array.isArray(parsed)
    ? parsed.filter((id): id is string => typeof id === 'string')
    : [];
}

function readConfirmedPushRegistrations(): Record<string, string> {
  const parsed: unknown = readStoredJson(
    CONFIRMED_PUSH_REGISTRATION_STORAGE_KEY,
    {},
  );
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  return Object.fromEntries(
    Object.entries(parsed).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    ),
  );
}

function readConfirmedPushRegistration(
  accountId: string | null,
  providerSubscriptionId: string | null,
): boolean {
  if (!accountId || !providerSubscriptionId) return false;
  return readConfirmedPushRegistrations()[accountId] === providerSubscriptionId;
}

function readConfirmedPushRegistrationOwner(
  providerSubscriptionId: string | null,
): string | null {
  if (!providerSubscriptionId) return null;
  const entry = Object.entries(readConfirmedPushRegistrations()).find(
    ([, id]) => id === providerSubscriptionId,
  );
  return entry?.[0] ?? null;
}

function writeConfirmedPushRegistration(
  accountId: string | null,
  providerSubscriptionId: string | null,
): void {
  if (!accountId || !providerSubscriptionId) return;
  try {
    localStorage.setItem(
      CONFIRMED_PUSH_REGISTRATION_STORAGE_KEY,
      JSON.stringify({
        ...readConfirmedPushRegistrations(),
        [accountId]: providerSubscriptionId,
      }),
    );
  } catch {
    // Storage can be unavailable (private mode, quota): the in-memory map
    // still covers this page session.
  }
}

function clearConfirmedPushRegistration(accountId: string | null): void {
  if (!accountId) return;
  try {
    const registrations = readConfirmedPushRegistrations();
    delete registrations[accountId];
    if (Object.keys(registrations).length === 0) {
      localStorage.removeItem(CONFIRMED_PUSH_REGISTRATION_STORAGE_KEY);
    } else {
      localStorage.setItem(
        CONFIRMED_PUSH_REGISTRATION_STORAGE_KEY,
        JSON.stringify(registrations),
      );
    }
  } catch {
    // Storage can be unavailable (private mode, quota): best-effort cleanup.
  }
}

function readPendingPushRegistration(accountId: string | null): boolean {
  return accountId !== null && readPendingPushAccountIds().includes(accountId);
}

function writePendingPushRegistration(
  accountId: string | null,
  pending: boolean,
): void {
  // Without a known account the marker would be unscoped and could later
  // mislead a different account's switch; the disable path is unreachable
  // before the session user is loaded, so skipping is safe.
  if (!accountId) return;
  try {
    const ids = new Set(readPendingPushAccountIds());
    if (pending) {
      ids.add(accountId);
    } else {
      ids.delete(accountId);
    }
    if (ids.size === 0) {
      localStorage.removeItem(PENDING_PUSH_REGISTRATION_STORAGE_KEY);
    } else {
      localStorage.setItem(
        PENDING_PUSH_REGISTRATION_STORAGE_KEY,
        JSON.stringify([...ids]),
      );
    }
  } catch {
    // Storage can be unavailable (private mode, quota): the in-memory
    // map still covers this page session.
  }
}

function markPushRegistrationPending(
  oneSignal: OneSignalSdk,
  accountId: string | null,
): void {
  if (!accountId) return;
  const ids = pushRegistrationsPending.get(oneSignal) ?? new Set<string>();
  ids.add(accountId);
  pushRegistrationsPending.set(oneSignal, ids);
}

function clearPushRegistrationPending(
  oneSignal: OneSignalSdk,
  accountId: string | null,
): void {
  if (!accountId) return;
  const ids = pushRegistrationsPending.get(oneSignal);
  if (!ids) return;
  ids.delete(accountId);
  if (ids.size === 0) pushRegistrationsPending.delete(oneSignal);
}

function markPushRegistrationConfirmed(
  oneSignal: OneSignalSdk,
  accountId: string | null,
  providerSubscriptionId: string | null,
): void {
  if (!accountId || !providerSubscriptionId) return;
  const ids =
    confirmedPushRegistrations.get(oneSignal) ?? new Map<string, string>();
  ids.set(accountId, providerSubscriptionId);
  confirmedPushRegistrations.set(oneSignal, ids);
}

function ensureOneSignalSdkInitialized(oneSignal: OneSignalSdk): Promise<void> {
  // Share the init result across component instances: a recreated component
  // must observe the same promise instead of calling the v16 SDK's one-time
  // `init()` again, which would reject as already initialized and strand state
  // detection, registration, and cleanup for the rest of the page session.
  let initPromise = oneSignalInitPromises.get(oneSignal);
  if (!initPromise) {
    initPromise = oneSignal
      .init({ appId: environment.oneSignalAppId })
      .catch((error: unknown) => {
        oneSignalInitPromises.delete(oneSignal);
        throw error;
      });
    oneSignalInitPromises.set(oneSignal, initPromise);
  }
  return initPromise;
}

function loadOneSignalSdk(): Promise<OneSignalSdk> {
  // The script element is document-wide, so the load promise is shared by every
  // component instance and by logout cleanup.
  oneSignalSdkPromise ??= new Promise((resolve, reject) => {
    window.OneSignalDeferred = window.OneSignalDeferred ?? [];
    window.OneSignalDeferred.push(resolve);

    const existingScript = document.getElementById(ONE_SIGNAL_SCRIPT_ID);
    if (existingScript) return;

    const script = document.createElement('script');
    script.id = ONE_SIGNAL_SCRIPT_ID;
    script.src = ONE_SIGNAL_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      // Drop the dead script tag and cached rejection so the next attempt can
      // re-download the SDK instead of reusing this failure forever.
      script.remove();
      oneSignalSdkPromise = undefined;
      reject(new Error('Failed to load OneSignal SDK.'));
    };
    document.head.appendChild(script);
  });
  return oneSignalSdkPromise;
}

export async function disconnectCurrentBrowserPushSubscription(
  accountId: string | null,
): Promise<void> {
  // Invalidate any registration that started before this cleanup, before the
  // first await, so a permission prompt that resolves later can neither opt
  // this browser back in nor confirm a registration for the ended session.
  pushCleanupEpoch += 1;
  // Forget the confirmed association, but leave a pending marker: the browser
  // is now opted out for this account, so the next switch click must
  // re-register this browser (restoring it) instead of reading the surviving
  // local subscription as proof this browser receives the account's push and
  // disabling push account-wide.
  clearConfirmedPushRegistration(accountId);
  writePendingPushRegistration(accountId, true);

  if (!environment.oneSignalAppId || typeof window === 'undefined') return;

  try {
    const oneSignal = await loadOneSignalSdk();
    await ensureOneSignalSdkInitialized(oneSignal);
    if (accountId) {
      markPushRegistrationPending(oneSignal, accountId);
      const registrations = confirmedPushRegistrations.get(oneSignal);
      registrations?.delete(accountId);
      if (registrations?.size === 0)
        confirmedPushRegistrations.delete(oneSignal);
    }
    await oneSignal.User.PushSubscription.optOut?.();
  } catch {
    // Logout must not be blocked by browser push cleanup.
  }
}

@Component({
  selector: 'app-account-settings',
  standalone: true,
  templateUrl: './account-settings.component.html',
})
export class AccountSettingsComponent {
  private readonly authState = inject(AuthStateService);
  private readonly router = inject(Router);
  private readonly updateNotificationPreferenceGql = inject(
    UpdateNotificationPreferenceGQL,
  );
  private readonly registerPushSubscriptionGql = inject(
    RegisterPushSubscriptionGQL,
  );
  /** Monotonic id per switch so a slower write cannot roll back a newer one. */
  private readonly overrideGenerations = new Map<string, number>();
  /**
   * Confirmation refreshes this instance has started but whose outcome has
   * not arrived yet. A failed confirmation only retries while this is zero -
   * retrying with another confirmation in flight would supersede that
   * refresh's response, and if the retry then failed too, both saved
   * settings would stay masked by their optimistic overrides.
   */
  private activeConfirmations = 0;
  /**
   * Confirmations that failed while another confirmation was still in
   * flight. They defer instead of retrying (that would supersede the healthy
   * refresh) and are replayed one at a time once the component is idle
   * again, so a busy moment never silently consumes their retry.
   */
  private readonly deferredConfirms: Array<() => void> = [];

  readonly user = computed(
    () => this.authState.user() as CurrentUserQuery['me'] | null,
  );
  readonly overrides = signal<NotificationOverride>({});
  readonly pendingKeys = signal<ReadonlySet<string>>(new Set());
  readonly emailNotifications = computed(() =>
    this.preferenceEnabled(
      NotificationPreferenceTopic.Marketing,
      'emailEnabled',
      true,
    ),
  );
  readonly smsNotifications = computed(() =>
    this.preferenceEnabled(
      NotificationPreferenceTopic.OrderUpdates,
      'smsEnabled',
      true,
    ),
  );
  readonly pushNotifications = computed(() =>
    this.preferenceEnabled(
      NotificationPreferenceTopic.OrderUpdates,
      'pushEnabled',
      false,
    ),
  );
  readonly notice = signal<string | null>(null);

  toggleEmail(): void {
    const accountId = this.user()?.id ?? null;
    if (!accountId) {
      this.setPushUpdateFailedNotice();
      return;
    }
    this.updatePreference(
      NotificationPreferenceTopic.Marketing,
      'emailEnabled',
      !this.emailNotifications(),
      accountId,
    );
  }

  toggleSms(): void {
    const accountId = this.user()?.id ?? null;
    if (!accountId) {
      this.setPushUpdateFailedNotice();
      return;
    }
    this.updatePreference(
      NotificationPreferenceTopic.OrderUpdates,
      'smsEnabled',
      !this.smsNotifications(),
      accountId,
    );
  }

  async togglePush(): Promise<void> {
    const topic = NotificationPreferenceTopic.OrderUpdates;
    const channel: NotificationChannelField = 'pushEnabled';
    const key = this.pendingKey(topic, channel);

    // Mark in flight before the first await so a second click during the
    // SDK load / permission prompt cannot start a duplicate registration.
    if (this.pendingKeys().has(key)) return;
    this.notice.set(null);
    this.pendingKeys.update((current) => new Set(current).add(key));

    let handedOffToPreferenceMutation = false;
    try {
      const context = this.createPushToggleContext();
      if (!context) return;
      const action = await this.resolvePushToggleAction(context);
      if (!action) return;
      handedOffToPreferenceMutation = await this.runPushToggleAction(
        action,
        context,
      );
      // A failure of any kind (OneSignal SDK unavailable, permission refused,
      // backend registration error, unsupported browser) leaves the account
      // preference unchanged: a local failure in one browser must never
      // disable push for the user's other devices. The helper set a specific
      // notice and the finally block releases the pending key.
    } catch {
      this.setPushUpdateFailedNotice();
    } finally {
      // updatePreference owns the key (and clears it when the mutation
      // settles); every other exit path must release it here.
      if (!handedOffToPreferenceMutation) this.clearPendingKey(key);
    }
  }

  isPending(topic: string, channel: NotificationChannelField): boolean {
    return this.pendingKeys().has(this.pendingKey(topic, channel));
  }

  private createPushToggleContext(): PushToggleContext | null {
    const accountEnabled = this.pushNotifications();
    // Capture the account that initiated this click before any SDK load or
    // permission prompt can outlive the signed-in user. Pending registration
    // markers must describe that initiating account even if the session is
    // cleared while the browser prompt is open.
    const accountId = this.user()?.id ?? null;
    if (!this.currentAccountMatches(accountId)) {
      this.setPushUpdateFailedNotice();
      return null;
    }
    // Capture the cleanup epoch before the toggle's first await (the SDK load
    // in getDevicePushState): a logout cleanup that starts at any point during
    // this toggle must invalidate it instead of becoming its baseline.
    return { accountEnabled, accountId, cleanupEpoch: pushCleanupEpoch };
  }

  private async resolvePushToggleAction(
    context: PushToggleContext,
  ): Promise<PushToggleAction | null> {
    const deviceState = await this.getDevicePushState();
    if (!this.ensureCurrentAccountStillMatches(context.accountId)) return null;
    // No account preference, or no local subscription: register this browser
    // instead of disabling push account-wide. A logout-created opt-out is
    // handled by canDisablePushForAccount below: cleanup leaves a
    // pending-registration marker for the account, so clicking the on-looking
    // switch re-registers this browser (restoring it) rather than disabling
    // push for the user's other devices.
    if (!context.accountEnabled || deviceState === 'none') {
      return 'register';
    }
    const canDisable = await this.canDisablePushForAccount(
      context.accountId,
      context.cleanupEpoch,
    );
    if (canDisable === null) return null;
    return canDisable ? 'disable' : 'register';
  }

  private async canDisablePushForAccount(
    accountId: string,
    cleanupEpoch: number,
  ): Promise<boolean | null> {
    const pendingRegistration =
      await this.hasPendingPushRegistration(accountId);
    if (!this.ensureCurrentAccountStillMatches(accountId)) return null;
    if (pendingRegistration) return false;

    const confirmedRegistration =
      await this.hasConfirmedPushRegistration(accountId);
    if (!this.ensureCurrentAccountStillMatches(accountId)) return null;
    if (confirmedRegistration) return true;

    const verifiedRegistration = await this.confirmExistingPushRegistration(
      accountId,
      cleanupEpoch,
    );
    if (!this.ensureCurrentAccountStillMatches(accountId)) return null;
    return verifiedRegistration ? true : null;
  }

  private async runPushToggleAction(
    action: PushToggleAction,
    context: PushToggleContext,
  ): Promise<boolean> {
    if (action === 'disable') {
      return this.disablePushForCurrentAccount(
        context.accountId,
        context.cleanupEpoch,
      );
    }
    return this.registerPushForCurrentAccount(context);
  }

  private async disablePushForCurrentAccount(
    accountId: string,
    cleanupEpoch: number,
  ): Promise<boolean> {
    if (!(await this.refreshCurrentAccountMatches(accountId))) {
      this.setPushUpdateFailedNotice();
      return false;
    }
    if (pushCleanupEpoch !== cleanupEpoch) {
      // Logout cleanup ran during this toggle; do not change the account
      // preference for a session that has since ended.
      this.setPushUpdateFailedNotice();
      return false;
    }
    this.updatePreference(
      NotificationPreferenceTopic.OrderUpdates,
      'pushEnabled',
      false,
      accountId,
    );
    return true;
  }

  private async registerPushForCurrentAccount(
    context: PushToggleContext,
  ): Promise<boolean> {
    // Register this browser independently of the account flag: the flag may
    // already be on because another device enabled it, or off entirely.
    const registered = await this.registerBrowserPushSubscription(
      context.accountId,
      context.cleanupEpoch,
    );
    if (!registered) return false;
    if (!(await this.refreshCurrentAccountMatches(context.accountId))) {
      this.setPushUpdateFailedNotice();
      return false;
    }
    if (context.accountEnabled) {
      this.notice.set('This browser is now subscribed to push updates.');
      return false;
    }
    this.updatePreference(
      NotificationPreferenceTopic.OrderUpdates,
      'pushEnabled',
      true,
      context.accountId,
    );
    return true;
  }

  private ensureCurrentAccountStillMatches(accountId: string): boolean {
    if (this.currentAccountMatches(accountId)) return true;
    this.setPushUpdateFailedNotice();
    return false;
  }

  private setPushUpdateFailedNotice(): void {
    this.notice.set(
      'We could not update push notifications. Please try again.',
    );
  }

  private currentAccountMatches(accountId: string | null): accountId is string {
    return accountId !== null && this.user()?.id === accountId;
  }

  private async refreshCurrentAccountMatches(
    accountId: string | null,
  ): Promise<boolean> {
    if (!this.currentAccountMatches(accountId)) return false;
    const authenticated = await firstValueFrom(
      this.authState.ensureSession(true),
    );
    if (!authenticated && this.authState.status() === 'unauthenticated') {
      this.bounceToLogin();
      return false;
    }
    return authenticated && this.currentAccountMatches(accountId);
  }

  private updatePreference(
    topic: NotificationPreferenceTopic,
    channel: NotificationChannelField,
    enabled: boolean,
    expectedUserId: string,
  ): void {
    const key = this.pendingKey(topic, channel);
    // Identifies this write: a slower mutation or refresh from an earlier
    // click must not erase the override of a newer toggle of the same switch.
    const generation = this.bumpOverrideGeneration(key);
    this.notice.set(null);
    this.overrides.update((current) =>
      this.setOverride(current, topic, channel, enabled),
    );
    this.pendingKeys.update((current) => new Set(current).add(key));

    this.updateNotificationPreferenceGql
      .mutate({
        variables: {
          input: { expectedUserId, topic, [channel]: enabled },
        },
      })
      .subscribe({
        next: () => {
          if (this.overrideGeneration(key) !== generation) return;
          this.clearPendingKey(key);
          // Server confirmed: refresh, then release settled optimistic
          // overrides so the refreshed server values become the source of
          // truth instead of being masked by the overrides. One retry covers
          // a transient refresh failure so a saved switch is not left masked
          // behind an override that never got confirmed.
          let retried = false;
          this.activeConfirmations++;

          const startRefresh = () => {
            this.activeConfirmations++;
            this.authState.ensureSession(true).subscribe({
              next: confirm,
              error: () => this.activeConfirmations--,
            });
          };

          const onUnconfirmed = () => {
            // Only a live session is worth retrying; a rejection bounces in
            // `confirm` and an unknown state has nothing to reconcile yet.
            if (this.authState.status() !== 'authenticated') return;
            if (retried) {
              // This confirmation already used its retry. If it was the last
              // one in flight, give a confirmation that deferred earlier its
              // serial turn - any later success sweeps every settled
              // override at once.
              if (this.activeConfirmations === 0) {
                this.runDeferredConfirms();
              }
              return;
            }
            if (this.activeConfirmations > 0) {
              // Another confirmation is in flight: retrying now would
              // supersede its response. Defer instead of losing the retry -
              // the last confirmation to settle replays it.
              this.deferredConfirms.push(() => {
                if (this.overrideGeneration(key) === generation) {
                  onUnconfirmed();
                }
              });
              return;
            }
            retried = true;
            startRefresh();
          };

          const confirm = (authenticated: boolean) => {
            this.activeConfirmations--;
            if (
              !authenticated &&
              this.authState.status() === 'unauthenticated'
            ) {
              // The session was rejected while this page is mounted. Checked
              // before the generation guard so a superseded confirmation
              // cannot swallow it: this refresh skips global error handling
              // and the auth guard only runs on navigation, so nothing else
              // would ever leave the page.
              this.deferredConfirms.length = 0;
              this.bounceToLogin();
              return;
            }
            // A newer toggle took over while the refresh was in flight; that
            // toggle's own refresh will release the override. If this was
            // the last confirmation in flight, deferred retries would be
            // stranded behind an exited confirmation whenever the newer
            // mutation never starts its own confirmation (it failed, or is
            // still pending) - drain them so an already-saved setting still
            // gets reconciled instead of staying masked for the lifetime of
            // the component.
            if (this.overrideGeneration(key) !== generation) {
              if (this.activeConfirmations === 0) {
                this.runDeferredConfirms();
              }
              return;
            }
            if (!authenticated) {
              onUnconfirmed();
              return;
            }
            // A successful confirmation saw every mutation committed before
            // it started, so its snapshot reconciles every settled override
            // at once - nothing deferred needs to confirm anymore.
            this.deferredConfirms.length = 0;
            this.overrides.update((current) =>
              this.removeConfirmedOverrides(current),
            );
          };

          this.authState.ensureSession(true).subscribe({
            next: confirm,
            // Defensive: ensureSession normalizes failures to `false`; if it
            // ever errors, keep the override and release the in-flight slot.
            error: () => this.activeConfirmations--,
          });
        },
        error: () => {
          // Only the write that still owns the switch may roll back its
          // optimistic state; a newer toggle keeps its override and pending key.
          if (this.overrideGeneration(key) === generation) {
            this.overrides.update((current) =>
              this.removeOverride(current, topic, channel),
            );
            this.clearPendingKey(key);
          }
          this.notice.set(
            'We could not save that notification setting. Please try again.',
          );
        },
      });
  }

  private bumpOverrideGeneration(key: string): number {
    const generation = this.overrideGeneration(key) + 1;
    this.overrideGenerations.set(key, generation);
    return generation;
  }

  private overrideGeneration(key: string): number {
    return this.overrideGenerations.get(key) ?? 0;
  }

  /**
   * Mirrors the auth guard / global error link: send the user to login with
   * the current page as the return target once the session is known dead.
   */
  private bounceToLogin(): void {
    const currentUrl = this.router.url;
    if (currentUrl.startsWith('/login') || currentUrl.startsWith('/register')) {
      return;
    }
    void this.router.navigate(['/login'], {
      queryParams: { redirectTo: currentUrl },
    });
  }

  /**
   * Replay deferred confirmation retries one at a time: each replay either
   * starts a refresh (ending the loop) or was superseded (skipped), so a
   * burst of failures produces serial retries instead of parallel ones that
   * would invalidate each other.
   */
  private runDeferredConfirms(): void {
    while (this.activeConfirmations === 0) {
      const next = this.deferredConfirms.shift();
      if (!next) return;
      next();
    }
  }

  private preferenceEnabled(
    topic: NotificationPreferenceTopic,
    channel: NotificationChannelField,
    fallback: boolean,
  ): boolean {
    const override = this.overrides()[topic]?.[channel];
    if (override !== undefined && override !== null) return override;

    return this.serverPreferenceValue(topic, channel) ?? fallback;
  }

  /** Canonical server value for a channel, or undefined when absent. */
  private serverPreferenceValue(
    topic: NotificationPreferenceTopic,
    channel: NotificationChannelField,
  ): boolean | undefined {
    const preference = this.user()?.notificationPreferences?.find(
      (candidate) => candidate.topic === topic,
    );
    const value = preference?.[channel];
    return typeof value === 'boolean' ? value : undefined;
  }

  private pendingKey(topic: string, channel: NotificationChannelField): string {
    return `${topic}:${channel}`;
  }

  private clearPendingKey(key: string): void {
    this.pendingKeys.update((current) => {
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  }

  private async registerBrowserPushSubscription(
    accountId: string,
    cleanupEpoch: number,
  ): Promise<boolean> {
    if (!this.browserPushAvailable()) {
      this.notice.set(
        'Push notifications are not available in this browser yet.',
      );
      return false;
    }

    if (!environment.oneSignalAppId) {
      this.notice.set('Push notifications are not configured yet.');
      return false;
    }

    let oneSignal: OneSignalSdk;
    try {
      oneSignal = await this.loadOneSignal();
      await this.ensureOneSignalInitialized(oneSignal);
    } catch {
      // The SDK could not be downloaded or initialised: this browser's push
      // state cannot be determined and registration is impossible.
      this.notice.set(
        'We could not enable push notifications. Please try again.',
      );
      return false;
    }

    try {
      const providerSubscriptionId = await this.registerOneSignalSubscription(
        oneSignal,
        accountId,
        cleanupEpoch,
      );

      if (!providerSubscriptionId) {
        // registerOneSignalSubscription sets a specific notice (permission
        // prompt refused); only fall back to a generic one when it could not
        // (opt-in produced no subscription id).
        if (!this.notice()) {
          this.notice.set(
            'We could not register this browser for push notifications.',
          );
        }
        return false;
      }

      if (pushCleanupEpoch !== cleanupEpoch) {
        // Logout cleanup ran while the SDK load / prompt / opt-in was in
        // flight. It already opted this browser out and rewrote the
        // registration markers, so do not write them back or confirm a stale
        // registration for a session that has since ended.
        this.notice.set(
          'We could not enable push notifications. Please try again.',
        );
        return false;
      }

      // Local opt-in succeeded. Until the backend stores the id, a surviving
      // local subscription would otherwise read as "this browser receives
      // push" on the next click - remember to re-associate instead. Cleared
      // only once the backend has actually stored the subscription. Scoped to
      // the signed-in account so another account's switch on this browser is
      // unaffected.
      markPushRegistrationPending(oneSignal, accountId);
      writePendingPushRegistration(accountId, true);

      if (!(await this.refreshCurrentAccountMatches(accountId))) {
        this.notice.set(
          'We could not enable push notifications. Please try again.',
        );
        return false;
      }

      await this.storePushSubscription(accountId, providerSubscriptionId);
      if (pushCleanupEpoch !== cleanupEpoch) {
        // Logout cleanup ran while the backend write was in flight. It already
        // opted this browser out and rewrote the markers, so do not clear its
        // pending marker or confirm a registration for the ended session -
        // otherwise the restored confirmation would make the next toggle
        // disable push account-wide instead of restoring this browser.
        this.notice.set(
          'We could not enable push notifications. Please try again.',
        );
        return false;
      }
      clearPushRegistrationPending(oneSignal, accountId);
      writePendingPushRegistration(accountId, false);
      markPushRegistrationConfirmed(
        oneSignal,
        accountId,
        providerSubscriptionId,
      );
      writeConfirmedPushRegistration(accountId, providerSubscriptionId);
      return true;
    } catch {
      this.notice.set(
        'We could not enable push notifications. Please try again.',
      );
      return false;
    }
  }

  private async confirmExistingPushRegistration(
    accountId: string,
    cleanupEpoch: number,
  ): Promise<boolean> {
    let oneSignal: OneSignalSdk;
    try {
      oneSignal = await this.loadOneSignal();
    } catch {
      this.notice.set(
        'We could not enable push notifications. Please try again.',
      );
      return false;
    }

    const providerSubscriptionId = oneSignal.User.PushSubscription.id ?? null;
    if (!providerSubscriptionId) return false;

    const storedOwner = readConfirmedPushRegistrationOwner(
      providerSubscriptionId,
    );
    if (storedOwner && storedOwner !== accountId) {
      this.notice.set(
        'This browser is already registered for another account.',
      );
      return false;
    }

    try {
      if (!(await this.refreshCurrentAccountMatches(accountId))) {
        this.notice.set(
          'We could not update push notifications. Please try again.',
        );
        return false;
      }
      await this.storePushSubscription(accountId, providerSubscriptionId);
      if (pushCleanupEpoch !== cleanupEpoch) {
        // Logout cleanup ran while the backend write was in flight; do not
        // confirm a registration for the session that just ended.
        this.notice.set(
          'We could not update push notifications. Please try again.',
        );
        return false;
      }
      markPushRegistrationConfirmed(
        oneSignal,
        accountId,
        providerSubscriptionId,
      );
      writeConfirmedPushRegistration(accountId, providerSubscriptionId);
      return true;
    } catch {
      this.notice.set(
        'This browser could not be verified for this account. Please try again.',
      );
      return false;
    }
  }

  private storePushSubscription(
    expectedUserId: string,
    providerSubscriptionId: string,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.registerPushSubscriptionGql
        .mutate({
          variables: {
            input: {
              expectedUserId,
              providerSubscriptionId,
              userAgent: navigator.userAgent,
            },
          },
        })
        .subscribe({
          next: ({ data }) => {
            // The backend answers `false` when it does not store the
            // subscription: report failure instead of claiming success and
            // letting the caller enable the account preference for a browser
            // that was never registered.
            if (data?.registerPushSubscription) {
              resolve();
            } else {
              reject(new Error('Push subscription was not registered.'));
            }
          },
          error: reject,
        });
    });
  }

  private browserPushAvailable(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof document !== 'undefined' &&
      'Notification' in window
    );
  }

  /**
   * True when this browser was opted in locally but the backend never stored
   * the subscription: the next click must retry the association instead of
   * reading the surviving local subscription as "this browser receives the
   * account's push" and disabling the account preference.
   */
  private async hasPendingPushRegistration(
    accountId: string,
  ): Promise<boolean> {
    if (!environment.oneSignalAppId || !this.browserPushAvailable()) {
      return false;
    }
    // The storage marker outlives reloads; the in-memory map covers sessions
    // where storage is unavailable. Both are scoped to this account, so a
    // pending registration left by another account on this browser does not
    // block this account's disable path.
    if (readPendingPushRegistration(accountId)) return true;
    try {
      const ids = pushRegistrationsPending.get(await this.loadOneSignal());
      return ids?.has(accountId) ?? false;
    } catch {
      // Without a loaded SDK there can be no surviving local subscription,
      // so nothing is pending; getDevicePushState reports 'none' anyway.
      return false;
    }
  }

  private async hasConfirmedPushRegistration(
    accountId: string,
  ): Promise<boolean> {
    if (!environment.oneSignalAppId || !this.browserPushAvailable()) {
      return false;
    }
    try {
      const oneSignal = await this.loadOneSignal();
      const providerSubscriptionId = oneSignal.User.PushSubscription.id ?? null;
      const memoryIds = confirmedPushRegistrations.get(oneSignal);
      return (
        memoryIds?.get(accountId) === providerSubscriptionId ||
        readConfirmedPushRegistration(accountId, providerSubscriptionId)
      );
    } catch {
      return false;
    }
  }

  /** Local (this browser) subscription state, independent of the account flag. */
  private async getDevicePushState(): Promise<DevicePushState> {
    if (!environment.oneSignalAppId || !this.browserPushAvailable()) {
      return 'none';
    }

    try {
      const oneSignal = await this.loadOneSignal();
      await this.ensureOneSignalInitialized(oneSignal);
      const subscription = oneSignal.User.PushSubscription;
      if (typeof subscription.id !== 'string') return 'none';
      // An id survives opting out, so it alone does not prove this browser
      // still receives push.
      if (subscription.optedIn !== false) return 'subscribed';
      // Opted out: permission still granted means push was switched off for
      // this browser deliberately; permission revoked (the common opt-out
      // cause) leaves this browser simply unsubscribed so registration may
      // prompt to opt back in.
      return oneSignal.Notifications.permission ? 'opted-out' : 'none';
    } catch {
      return 'none';
    }
  }

  private ensureOneSignalInitialized(oneSignal: OneSignalSdk): Promise<void> {
    return ensureOneSignalSdkInitialized(oneSignal);
  }

  private async registerOneSignalSubscription(
    oneSignal: OneSignalSdk,
    initiatingAccountId: string,
    cleanupEpoch: number,
  ): Promise<string | null> {
    if (!oneSignal.Notifications.permission) {
      // OneSignal v16 resolves after the prompt without necessarily returning
      // the resulting boolean, so the permission state is read back instead
      // of trusting the awaited value: a user who accepts the prompt must
      // still reach opt-in and backend registration.
      await oneSignal.Notifications.requestPermission();
    }
    if (!oneSignal.Notifications.permission) {
      this.notice.set(
        'Allow browser notifications before enabling push updates.',
      );
      return null;
    }

    // Check before optIn (after the prompt), because the prompt can outlive
    // the session that started it:
    //  - a logout during the prompt bumps the cleanup epoch, and its cleanup
    //    has already opted this browser out;
    //  - the session may have ended (status no longer authenticated);
    //  - account A may have been replaced by account B, whose session must
    //    not opt A's subscription back in.
    // Any of these makes this a stale registration that must not opt the
    // browser back in after cleanup ran.
    const currentAccountId = this.user()?.id ?? null;
    const cleanupRan = pushCleanupEpoch !== cleanupEpoch;
    const accountReplaced =
      this.authState.status() !== 'authenticated' ||
      currentAccountId !== initiatingAccountId;
    if (cleanupRan || accountReplaced) {
      this.notice.set(
        'We could not enable push notifications. Please try again.',
      );
      return null;
    }

    await oneSignal.User.PushSubscription.optIn();
    return oneSignal.User.PushSubscription.id ?? null;
  }

  private loadOneSignal(): Promise<OneSignalSdk> {
    return loadOneSignalSdk();
  }

  /**
   * Drop every optimistic override whose write has settled (no pending key)
   * and whose refreshed server value is present. A confirmed refresh snapshot
   * was taken after those mutations committed, so the server values include
   * them - this also releases an override whose own confirmation refresh was
   * superseded by a newer refresh that failed before it could confirm.
   */
  private removeConfirmedOverrides(
    current: NotificationOverride,
  ): NotificationOverride {
    let next = current;
    for (const topic of Object.keys(next) as NotificationPreferenceTopic[]) {
      const channels = next[topic];
      if (!channels) continue;
      for (const channel of Object.keys(
        channels,
      ) as NotificationChannelField[]) {
        if (this.pendingKeys().has(this.pendingKey(topic, channel))) continue;
        if (this.serverPreferenceValue(topic, channel) === undefined) continue;
        next = this.removeOverride(next, topic, channel);
      }
    }
    return next;
  }

  private removeOverride(
    current: NotificationOverride,
    topic: NotificationPreferenceTopic,
    channel: NotificationChannelField,
  ): NotificationOverride {
    const topicOverrides = current[topic];
    if (!topicOverrides) return current;

    const nextTopicOverrides = { ...topicOverrides };
    delete nextTopicOverrides[channel];

    if (Object.keys(nextTopicOverrides).length === 0) {
      const next = { ...current };
      delete next[topic];
      return next;
    }

    return { ...current, [topic]: nextTopicOverrides };
  }

  private setOverride(
    current: NotificationOverride,
    topic: NotificationPreferenceTopic,
    channel: NotificationChannelField,
    enabled: boolean,
  ): NotificationOverride {
    const topicOverrides = current[topic];
    return {
      ...current,
      [topic]: topicOverrides
        ? { ...topicOverrides, [channel]: enabled }
        : { [channel]: enabled },
    };
  }
}
